import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/app/api";

const STATUS_FILTERS = [
  ["", "All"], ["ready_for_partner_review", "Ready for review"], ["under_review", "Under review"],
  ["more_information_required", "Awaiting learner"], ["decision_pending", "Decision pending"],
  ["offer", "Offers"], ["declined", "Declined"], ["submitted_to_partner", "Submitted"], ["submission_failed", "Submission failed"],
];

export default function PartnerApplications() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const status = searchParams.get("status") || "";

  useEffect(() => {
    const params = status ? `?status=${status}` : "";
    api.get(`/partner/applications${params}`).then((response) => setData(response.data.applications)).catch(() => setData([]));
  }, [status]);

  const filtered = (data || []).filter((application) => {
    if (!search) return true;
    const needle = search.toLowerCase();
    return [application.applicant, application.programme, application.provider, application.reference]
      .filter(Boolean).some((value) => value.toLowerCase().includes(needle));
  });

  return (
    <div data-testid="partner-applications" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-ink">Applications</h1>
          <p className="mt-1 text-sm text-slatey">Applicants who came to you through OpenDegree.</p>
        </div>
        <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="partner-search" placeholder="Search applicant, programme, reference…"
          className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-cobalt" />
      </div>

      <div className="flex gap-2 overflow-x-auto">
        {STATUS_FILTERS.map(([value, label]) => (
          <button key={value} onClick={() => setSearchParams(value ? { status: value } : {})} data-testid={`partner-filter-${value || "all"}`}
            className={`min-w-max rounded-full px-4 py-2 text-xs font-bold ${status === value ? "bg-cobalt text-white" : "bg-white text-slatey border border-slate-200"}`}>{label}</button>
        ))}
      </div>

      {!data ? <p className="text-sm text-slatey">Loading…</p> : (
        <div className="overflow-x-auto rounded-3xl border border-slate-200 bg-white">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead><tr className="border-b border-slate-100 text-xs font-bold uppercase text-slatey">
              <th className="px-5 py-3">Applicant</th><th className="px-5 py-3">Programme</th><th className="px-5 py-3">University</th>
              <th className="px-5 py-3">Intake</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Last updated</th>
            </tr></thead>
            <tbody>
              {filtered.map((application, index) => (
                <tr key={application.id || index} className="border-b border-slate-50 hover:bg-surface">
                  <td className="px-5 py-3">
                    <Link to={`/app/partner/applications/${application.id}`} data-testid={`partner-application-${application.id}`} className="font-bold text-cobalt hover:underline">
                      {application.applicant}
                    </Link>
                    <p className="text-xs text-slatey">{application.reference}</p>
                  </td>
                  <td className="px-5 py-3 text-ink">{application.programme}</td>
                  <td className="px-5 py-3 text-slatey">{application.provider}</td>
                  <td className="px-5 py-3 text-slatey">{application.intake}</td>
                  <td className="px-5 py-3"><span className="rounded-full bg-surface px-3 py-1 text-xs font-bold capitalize text-ink" data-testid={`partner-application-status-${application.id}`}>{application.status.replaceAll("_", " ")}</span></td>
                  <td className="px-5 py-3 text-xs text-slatey">{application.updated_at?.slice(0, 16).replace("T", " ")}</td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-sm text-slatey">No applications match.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
